import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { IPAddress, Pool } from '@dev/ee-api/ee-api-models';

import { HideToast, ShowToast } from '@/store/toast/actions';
import {
    AddNewPoolError,
    AddNewPoolSuccess,
    DeletePoolError,
    DeletePoolSuccess,
    LoadAllIPsError,
    LoadAllIPsSuccess,
    LoadPoolsError,
    LoadPoolsSuccess,
    MoveIPToPoolError,
    MoveIPToPoolSuccess,
    UpdatePoolError,
    UpdatePoolSuccess,
} from '@/store/pools/actions';
import { addIPPool, deleteIPPool, loadAllIPs, loadIPPools, moveIpToPool, updateIPPool } from '@/services/ElasticApi';
import { AddNewPoolAction, DeletePoolAction, MoveIPToPoolAction, UpdatePoolAction } from '@/store/pools/types';
import { ApplicationState as State } from '@/store';
import { Logger } from '@/services/errors/appLogger';

const getUserIPPools = (state: State): Pool[] => state.pools.ipPools;
const getUserIPs = (state: State): IPAddress[] => state.pools.IPs;

function* fetchIPPools(): any {
    try {
        const ipPools = yield call(loadIPPools);
        yield put(LoadPoolsSuccess(ipPools));
    } catch (e) {
        yield put(LoadPoolsError());
        Logger.error(e?.response?.data?.Error);
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* fetchAllIPs(): any {
    try {
        const allIPs = yield call(loadAllIPs);
        yield put(LoadAllIPsSuccess(allIPs));
    } catch (e) {
        yield put(LoadAllIPsError());
        Logger.error(e?.response?.data?.Error);
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* addNewIPPool(action: AddNewPoolAction): any {
    const { payload } = action;
    try {
        const newPool = yield call(addIPPool, payload.name, payload.description);
        const userIPPools = yield select(getUserIPPools);
        userIPPools.push(newPool);
        yield put(AddNewPoolSuccess(userIPPools));
    } catch (e) {
        yield put(AddNewPoolError());
        Logger.error(e?.response?.data?.Error);
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}
function* editIPPool(action: UpdatePoolAction): any {
    const { payload } = action;
    const { oldName, ...newValues } = payload;
    const userIPPools = yield select(getUserIPPools);
    const userIPs = yield select(getUserIPs);
    const oldPoolValues = userIPPools.filter((el): boolean => el.Name === oldName)[0];
    const newPoolInfo = {
        ...oldPoolValues,
        ...newValues,
    };
    try {
        const updatePool = yield call(updateIPPool, oldName, newPoolInfo.name, newPoolInfo.description);

        const newPools = userIPPools.map((el): any => {
            if (el.Name === oldName) {
                return updatePool;
            }
            return el;
        });
        const newIPs = userIPs.map((el): any => {
            if (el.PoolName === oldName) {
                return { ...el, PoolName: newPoolInfo.name };
            }
            return el;
        });
        yield put(UpdatePoolSuccess(newPools));
        yield put(LoadAllIPsSuccess(newIPs));
    } catch (e) {
        yield put(UpdatePoolError());
        Logger.error(e?.response?.data?.Error);
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}
function* deleteSingleIPPool(action: DeletePoolAction): any {
    const { payload } = action;
    const userIPPools = yield select(getUserIPPools);

    try {
        const IpsFromDeletedPool = userIPPools?.filter((el): boolean => el.Name === payload.poolName)[0].IP;
        const remainingPools = userIPPools?.filter((el): boolean => el.Name !== payload.poolName);
        const usersIPs = yield select(getUserIPs);
        const updatedIPs = usersIPs?.reduce((acc, el): any => {
            if (IpsFromDeletedPool.includes(el.IP)) {
                const updatedIP = {
                    ...el,
                    PoolName: 'Default',
                };

                return [...acc, updatedIP];
            }
            return [...acc, el];
        }, []);
        const deletedPool = yield call(deleteIPPool, payload.poolName); //eslint-disable-line
        const updatedPools = remainingPools.map((el): Pool => {
            if (el.Name === 'Default') {
                return {
                    ...el,
                    IP: [...el.IP, ...IpsFromDeletedPool],
                };
            }
            return el;
        });

        yield put(DeletePoolSuccess(updatedPools));
        yield put(LoadAllIPsSuccess(updatedIPs));
        yield put(
            ShowToast({
                severity: 'info',
                message: `Pool deleted`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(DeletePoolError());
        Logger.error(e?.response?.data?.Error);
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}
function* migrateIPtoPool(action: MoveIPToPoolAction): any {
    const {
        payload: { ip, targetPool },
    } = action;
    const userIPs = yield select(getUserIPs);
    const userPools: Pool[] = yield select(getUserIPPools);
    const ipCurrentPoolName = userPools?.filter((el): boolean => el.IP.includes(ip))[0].Name;

    try {
        yield call(moveIpToPool, ip, targetPool);
        const updatedIPs = userIPs?.map((singleIp): IPAddress => {
            if (singleIp.IP === ip) {
                return { ...singleIp, PoolName: targetPool };
            }
            return singleIp;
        });
        const updatedPools = userPools.map((el): Pool => {
            if (el.Name === targetPool) {
                return {
                    ...el,
                    IP: [...el.IP, ip],
                };
            }
            if (el.Name === ipCurrentPoolName) {
                return {
                    ...el,
                    IP: el.IP.filter((singleIP): boolean => singleIP !== ip),
                };
            }
            return el;
        });
        yield put(UpdatePoolSuccess(updatedPools));
        yield put(MoveIPToPoolSuccess(updatedIPs));
    } catch (e) {
        yield put(MoveIPToPoolError());
        Logger.error(e?.response?.data?.Error);
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchForFetchIPPools(): any {
    yield takeEvery('@@pools/FETCH_POOLS', fetchIPPools);
}
function* watchForAddingNewPool(): any {
    yield takeEvery('@@pools/ADD_NEW_POOL', addNewIPPool);
}
function* watchForUpdatingPool(): any {
    yield takeEvery('@@pools/UPDATE_POOL', editIPPool);
}
function* watchForDeleteingPools(): any {
    yield takeEvery('@@pools/DELETE_POOL', deleteSingleIPPool);
}
function* watchForLoadingAllIPs(): any {
    yield takeEvery('@@pools/LOAD_ALL_IPS', fetchAllIPs);
}
function* watchForIPPoolChange(): any {
    yield takeEvery('@@pools/MOVE_IP_TO_POOL', migrateIPtoPool);
}

const poolsSagas = [
    fork(watchForFetchIPPools),
    fork(watchForAddingNewPool),
    fork(watchForUpdatingPool),
    fork(watchForDeleteingPools),
    fork(watchForLoadingAllIPs),
    fork(watchForIPPoolChange),
];

export default poolsSagas;
